import React, { useEffect, useState } from 'react';
import { supportFetch, fmtBytes, fmtNum } from '../api';
import StatCard from '../components/StatCard';

// Landing dashboard: headline metrics, a per-day upload trend, and auth summary.
const StatsTab = ({ token, onAuthFail }) => {
  const [overview, setOverview] = useState(null);
  const [uploads, setUploads]   = useState(null);
  const [auth, setAuth]         = useState(null);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(true);

  const load = () => {
    setLoading(true); setError('');
    Promise.all([
      supportFetch('/support/stats/overview', token, onAuthFail),
      supportFetch('/support/stats/uploads', token, onAuthFail),
      supportFetch('/support/stats/auth', token, onAuthFail),
    ])
      .then(([o, u, a]) => { setOverview(o); setUploads(u); setAuth(a); })
      .catch(e => { if (e.message !== 'Session expired') setError('Failed to load statistics.'); })
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="sp-loading">Loading statistics…</div>;
  if (error)   return <div className="sp-error">{error}</div>;
  if (!overview) return null;

  const maxDay = uploads?.byDay?.reduce((m, d) => Math.max(m, d.count + d.failures), 0) || 1;

  return (
    <div className="sp-stats">
      <div className="sp-toolbar">
        <button className="sp-btn" onClick={load}>Refresh</button>
        <span className="sp-muted">as of {new Date(overview.generatedAt).toLocaleTimeString()}</span>
      </div>

      <div className="sp-stat-grid">
        <StatCard label="Uploads today"   value={fmtNum(overview.uploadsToday)} />
        <StatCard label="Uploads 7d"      value={fmtNum(overview.uploads7d)} />
        <StatCard label="Uploads 30d"     value={fmtNum(overview.uploads30d)} />
        <StatCard label="Success rate"    value={overview.successRate == null ? '—' : `${overview.successRate}%`}
          sub={`${fmtNum(overview.uploadSuccess)} ok / ${fmtNum(overview.uploadFailure)} failed`}
          tone={overview.successRate != null && overview.successRate < 90 ? 'warn' : 'ok'} />
        <StatCard label="Errors (24h)"    value={fmtNum(overview.errors24h)} tone={overview.errors24h > 0 ? 'warn' : 'ok'} />
        <StatCard label="Active companies" value={fmtNum(overview.activeCompanies)} />
        <StatCard label="Total files"     value={fmtNum(overview.totalFiles)} />
        <StatCard label="Total storage"   value={fmtBytes(overview.totalBytes)} />
      </div>

      {uploads?.byDay?.length > 0 && (
        <div className="sp-panel">
          <h3>Upload volume (last 30 days)</h3>
          <div className="sp-bars">
            {uploads.byDay.map(d => (
              <div key={d.day} className="sp-bar-col" title={`${d.day}: ${d.count} uploads, ${d.failures} failed`}>
                <div className="sp-bar-stack">
                  <div className="sp-bar sp-bar--fail" style={{ height: `${(d.failures / maxDay) * 100}%` }} />
                  <div className="sp-bar sp-bar--ok"   style={{ height: `${(d.count / maxDay) * 100}%` }} />
                </div>
                <span className="sp-bar-label">{d.day.slice(5)}</span>
              </div>
            ))}
          </div>
          {uploads.byType && (
            <div className="sp-chips">
              {Object.entries(uploads.byType).map(([t, n]) => (
                <span key={t} className="sp-chip">{t}: {fmtNum(n)}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {auth?.roles && (
        <div className="sp-panel">
          <h3>Logins by role ({auth.range && `${auth.range.from.slice(0, 10)} → ${auth.range.to.slice(0, 10)}`})</h3>
          <table className="sp-table">
            <thead><tr><th>Role</th><th>Success</th><th>Failure</th></tr></thead>
            <tbody>
              {Object.entries(auth.roles).map(([role, v]) => (
                <tr key={role}>
                  <td>{role}</td>
                  <td>{fmtNum(v.success)}</td>
                  <td className={v.failure > 0 ? 'sp-cell-warn' : ''}>{fmtNum(v.failure)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StatsTab;
